<template>
  <div class="relative">
    <l-button
      ref="buttonRef"
      :type="isFinalStatus ? 'text' : 'secondary'"
      color="white"
      size="small"
      :icon="statusIcon[tab.deliveryOrder!.status]"
      @click.stop="moveToNextStatus"
      @click.right.prevent="() => (dropdownOpen = true)"
    >
      {{ statusNames[tab.deliveryOrder!.status] }}
    </l-button>

    <l-dropdown
      v-if="dropdownOpen"
      v-model:open="dropdownOpen"
      :options="options"
      :model-value="[tab.deliveryOrder!.status]"
      @update:model-value="moveToStatus($event[0] as DeliveryStatus)"
    />
    <teleport to="#appRoot" v-if="showCourierSelector">
      <courier-selector @close="showCourierSelector = false" @assign-courier="setToOnDelivery" />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { onLongPress } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LDropdown, type LIconName } from '@last/core-ui/paprika'

import { useTabs } from '@/composables/useTabs'
import CourierSelector from '@/modules/tabs/CourierSelector.vue'
import { useConfigStore } from '@/store/config'
import type { Courier, DeliveryStatus, Tab } from '@/types'

interface Props {
  tab: Tab
}

const props = defineProps<Props>()

const { t } = useI18n()

const configStore = useConfigStore()
const { config } = storeToRefs(configStore)

const { updateDeliveryOrderStatus } = useTabs(() => props.tab.id)

const buttonRef = useTemplateRef<HTMLElement>('buttonRef')

const showCourierSelector = ref(false)
const dropdownOpen = ref(false)

const statusOrder = computed(() => {
  let statuses = [
    'CREATED',
    'KITCHEN',
    'READY_TO_PICKUP',
    'ON_DELIVERY',
    'DELIVERED'
  ] as DeliveryStatus[]
  if (props.tab.pickupType !== 'ownDelivery') {
    statuses = statuses.filter(status => status !== 'ON_DELIVERY')
  }
  return statuses
})

const statusNames: Record<DeliveryStatus, string> = {
  CREATED: t('tabs.created'),
  KITCHEN: t('tabs.kitchen'),
  READY_TO_PICKUP: t('tabs.pickup'),
  ON_DELIVERY: t('tabs.on-delivery'),
  DELIVERED: t('tabs.delivered'),
  CANCELLED: t('tabs.cancelled'),
  CLOSED: t('tabs.closed')
} as const

const statusIcon: Record<DeliveryStatus, LIconName> = {
  CREATED: 'check',
  KITCHEN: 'cooking-hat',
  READY_TO_PICKUP: 'take-away',
  ON_DELIVERY: 'bike',
  DELIVERED: 'check-circle',
  CANCELLED: 'close',
  CLOSED: 'close'
} as const

const options = Object.entries(statusNames).map(([value, label]) => {
  return {
    icon: statusIcon[value as DeliveryStatus],
    label,
    value
  }
})

onLongPress(
  buttonRef,
  () => {
    dropdownOpen.value = true
  },
  {
    modifiers: {
      prevent: true
    }
  }
)

const isFinalStatus = computed(() => {
  return ['DELIVERED', 'CANCELLED'].includes(props.tab.deliveryOrder!.status)
})

function moveToNextStatus() {
  if (dropdownOpen.value) return
  const currentIndex = statusOrder.value.indexOf(props.tab.deliveryOrder!.status)
  if (currentIndex === -1) return
  const nextStatus = statusOrder.value[currentIndex + 1]
  if (nextStatus) {
    moveToStatus(nextStatus)
  }
}

function moveToStatus(newStatus: DeliveryStatus) {
  if (
    newStatus === 'ON_DELIVERY' &&
    (!config.value.shipmentProviders?.selected || config.value.manualShipment)
  ) {
    showCourierSelector.value = true
    return
  } else {
    updateDeliveryOrderStatus({
      newStatus
    })
  }
  dropdownOpen.value = false
}

function setToOnDelivery(courier: Courier) {
  updateDeliveryOrderStatus({
    newStatus: 'ON_DELIVERY',
    courier: courier
  })
  showCourierSelector.value = false
}
</script>
